import React, { memo, useCallback } from 'react';

import { Box, FormControl, Typography } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers/';
import type { DesktopDatePickerProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { Nullable } from '@provider-portal/common/types/types';
import { useLocaleForDatePicker } from '@provider-portal/common/hooks/useLocaleForDatePicker';
import { endOfDay } from 'date-fns';

interface OverridableProps<TDate = unknown>
  extends Omit<DesktopDatePickerProps<TDate>, 'label' | 'value' | 'onChange' | 'renderInput'> {}

interface StartPickerProps<TDate = unknown> extends OverridableProps<TDate> {}
interface EndPickerProps<TDate = unknown> extends Omit<OverridableProps<TDate>, 'renderDay'> {}

export type StartEndDatePickerProps = React.PropsWithChildren<{
  strings: {
    start: string;
    startDescription?: string;
    end: string;
    endDescription?: string;
    minEndDateMessageError?: string;
  };
  startDate: Nullable<number>;
  endDate: Nullable<number>;
  onChange: (startMS: Nullable<number>, endMS: Nullable<number>) => void;
  startPickerProps?: StartPickerProps<Date | number | null>;
  endPickerProps?: EndPickerProps<Date | number | null>;
}>;

export const StartEndDatePicker = memo((props: StartEndDatePickerProps) => {
  const { strings, startDate, endDate, onChange, startPickerProps, endPickerProps, children } =
    props;

  const localeForDatePicker = useLocaleForDatePicker();

  const handleStartDateChange = useCallback(
    (date: Date | number | null) => {
      const dateObject = date instanceof Date ? date.getTime() : date;

      onChange(dateObject, endDate);
    },
    [endDate, onChange],
  );

  const handleEndDateChange = useCallback(
    (date: Date | number | null) => {
      onChange(startDate, date ? endOfDay(date).getTime() : null);
    },
    [onChange, startDate],
  );

  return (
    <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeForDatePicker}>
        <Box flexGrow={1} minWidth={{ xs: '100%', md: '33%' }}>
          <FormControl fullWidth>
            <Box mb={1}>
              <Typography variant="subtitleS">{strings.startDescription}</Typography>
            </Box>
            <DesktopDatePicker
              {...startPickerProps}
              label={strings.start}
              value={startDate ? new Date(startDate) : null}
              onChange={handleStartDateChange}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'filled',
                },
                openPickerButton: {
                  'aria-label': 'change date',
                },
              }}
            />
          </FormControl>
        </Box>
        <Box flexGrow={1} minWidth={{ xs: '100%', md: '33%' }}>
          <FormControl fullWidth>
            <Box mb={1}>
              <Typography variant="subtitleS">{strings.endDescription}</Typography>
            </Box>
            <DesktopDatePicker
              {...endPickerProps}
              label={strings.end}
              value={endDate ? new Date(endDate) : null}
              onChange={handleEndDateChange}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'filled',
                },
                openPickerButton: {
                  'aria-label': 'change date',
                },
              }}
            />
          </FormControl>
        </Box>
        {children}
      </LocalizationProvider>
    </Box>
  );
});
